import { useMemo, useCallback } from "react";
import { CalculatedColumn, DataGridProps } from "../types";

export type DataGridConfig<R extends object> = ReturnType<typeof useDataGridConfig<R>>;

const noop = () => null;

export function useDataGridConfig<R extends object>(props: DataGridProps<R>) {
  const columnKeys = useMemo(() => props.columns.map(col => col.field), [props.columns]);

  const columnMap = useMemo(
    () =>
      props.columns.reduce<Record<string, CalculatedColumn<R>>>((map, col) => {
        const isGroupCell = (props.groupBy ?? []).includes(col.field);
        const noop = () => null;

        const valueGetter = (row: R) => row[col.field as keyof R];
        const renderCell = (row: R) => String(row[col.field as keyof R]);
        const groupValueGetter = ([row]: R[]) => (col.valueGetter ? col.valueGetter(row) : valueGetter(row));
        const renderGroupCell = ([row]: R[]) => (col.renderCell ? col.renderCell(row) : renderCell(row));

        return {
          ...map,
          [col.field]: {
            fixed: false,
            sortable: true,
            draggable: true,
            resizable: true,
            align: "left",
            colSpan: 0,
            valueGetter,
            groupValueGetter: isGroupCell ? groupValueGetter : noop,
            renderCell: renderCell,
            renderGroupCell: isGroupCell ? renderGroupCell : noop,
            ...col,
          },
        };
      }, {}),
    [props.columns, props.groupBy]
  );

  const groupColumns = useMemo(() => (props.groupBy ?? []).map(field => columnMap[field]).filter(Boolean), [props.groupBy, columnMap]);

  const rowKeyGetter = useCallback(
    (row: R): string => {
      const { rowId } = props;

      return typeof rowId === "string" ? (row[rowId as keyof R] as string) : rowId(row);
    },
    [props.rowId]
  );

  return {
    name: props.name,
    className: props.className,
    loading: props.loading ?? false,
    filtered: props.filtered ?? false,
    enableVirtualization: props.enableVirtualization ?? true,
    data: props.data,
    rowKeyGetter,
    groupKeyGetter: props.groupRowId,
    rowHeight: props.rowHeight ?? 52,
    headerRowHeight: props.headerRowHeight ?? 56,
    defaultSorting: props.defaultSorting ?? [],
    columnKeys,
    columnMap,
    emptyMessage: props.emptyMessage,
    filteredMessage: props.filteredMessage,
    groupColumns,
    onRowClick: props.onRowClick,
    onSortChange: props.onSortChange ?? noop,
    onCellClick: props.onCellClick,
    excludeColumns: props.excludeColumns ?? [],
    onScroll: props.onScroll,
    noData: props.noData,
    renderRow: props.renderRow,
    rowClass: props.rowClass,
  };
}
